import React from 'react';
import { Field as FormField } from 'react-final-form';
import { Box, Typography, TextField } from '@mui/material';
import {
	FIELD_DESCRIPTION,
	FIELD_TITLE,
	FIELD_TEXT,
	FIELD_TEXTAREA,
	FIELDS_INPUT_DECISION_RADIOGROUP,
	FIELDS_START_END_INPUTS,
	FIELDS_NOTE_INPUT_FIELD
} from 'constants/fields';
import DecisionRadioGroup from 'UI/Inputs/DecisionRadioGroup';
import StartEndInputs from 'UI/Inputs/StartEndInputs';
import NotesField from 'components/Inputs/NotesField';
import './FormFieldRenderer.scss';

const FormFieldRenderer = ({ field, index, translate }) => {
	const renderTitle = () => {
		return (
			<Typography variant="subtitle2" sx={{ fontWeight: 600, }}>
				{translate(field.value)}
			</Typography>
		);
	};

	const renderDescription = () => {
		return (
			<Typography variant="body2" color="text.secondary">
				{field.value}
			</Typography>
		);
	};

	const renderText = () => {
		const isRequired = field.required !== false; // Default to required unless explicitly set to false

		return (
			<Box>
				{field.label && (
					<Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
						{field.label}
						{isRequired && <span style={{ color: 'red' }}> *</span>}
					</Typography>
				)}
				<FormField
					name={field.name || `field-${index}`}
					validate={isRequired ? (value) => (value ? undefined : translate('This field is required') || 'This field is required') : undefined}
					render={({ input, meta }) => (
						<TextField
							{...input}
							type="text"
							placeholder={field.placeholder}
							disabled={field.disabled || false}
							fullWidth
							variant="outlined"
							size="small"
							error={meta.submitFailed && meta.error}
							helperText={meta.submitFailed && meta.error ? meta.error : ''}
						/>
					)}
				/>
			</Box>
		);
	};

	const renderTextarea = () => {
		const isRequired = field.required !== false; // Default to required unless explicitly set to false

		return (
			<Box>
				{field.label && (
					<Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
						{field.label}
						{isRequired && <span style={{ color: 'red' }}> *</span>}
					</Typography>
				)}
				<FormField
					name={field.name || `field-${index}`}
					validate={isRequired ? (value) => (value?.trim() ? undefined : translate('This field is required')) : undefined}
					render={({ input, meta }) => (
						<TextField
							{...input}
							multiline
							rows={4}
							placeholder={field.placeholder}
							disabled={field.disabled || false}
							fullWidth
							variant="outlined"
							size="small"
							error={meta.submitFailed && meta.error}
							helperText={meta.submitFailed && meta.error ? meta.error : ''}
						/>
					)}
				/>
			</Box>
		);
	};

	const renderDecisionRadioGroup = () => {
		return (
			<Box>
				{field.label && (
					<Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
						{field.label}
					</Typography>
				)}
				<FormField
					name={field.name || `decision-${index}`}
					render={({ input, meta }) => {
						let labels = [translate('Approve'), translate('Reject')];
						// THIS CODE NEED TO RECHECKED WHEN WE MAKE LABELS DYNAMIC, FOR NOW KEEP THIS COMMENTED
						//	try {
						// 	const parsed = JSON.parse(field.placeholder || '[]');
						// 	if (Array.isArray(parsed)) {
						// 		labels = parsed.map(label => translate(label));
						// 	}
						// } catch (e) { }
						return (
							<DecisionRadioGroup
								input={input}
								meta={meta}
								name={input ? input.name : field.name || `decision-${index}`}
								onChange={input ? input.onChange : undefined}
								disabled={field.disabled || false}
								labels={labels}
								translate={translate}
							/>
						);
					}}
				/>
			</Box>
		);
	};

	const renderStartEndInputs = () => {
		const isRequired = field.required !== false; // Default to required unless explicitly set to false

		return (
			<Box>
				{field.label && (
					<Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
						{field.label}
						{isRequired && <span style={{ color: 'red' }}> *</span>}
					</Typography>
				)}
				<FormField
					name={field.name || `startend-${index}`}
					validate={isRequired ? (value) => {
						if (!value) return translate('This field is required') || 'This field is required';
						try {
							const parsed = typeof value === 'string' ? JSON.parse(value) : value;
							if (!parsed.start || !parsed.end) {
								return translate('Both start and end dates are required');
							}
						} catch (e) {
							return translate('Invalid date range');
						}
						return undefined;
					} : undefined}
					render={({ input, meta }) => {
						let current = { start: '', end: '' };
						try {
							current = input && input.value && typeof input.value === 'string' ? JSON.parse(input.value) : input.value || { start: '', end: '' };
						} catch (e) {
							current = { start: '', end: '' };
						}

						const labels = {
							startLabel: translate('start'),
							endLabel: translate('end')
						};

						const handleChange = (next) => {
							const payload = JSON.stringify({ start: next.start || '', end: next.end || '' });
							if (input && input.onChange) input.onChange(payload);
						};

						return (
							<>
								<StartEndInputs
									input={{
										name: input && input.name,
										value: {
											start: current.start || '',
											end: current.end || ''
										},
										onChange: handleChange
									}}
									disabled={field.disabled || false}
									labels={labels}
									translate={translate}
									error={meta.submitFailed && meta.error}
								/>
								{meta.submitFailed && meta.error && (
									<Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
										{meta.error}
									</Typography>
								)}
							</>
						);
					}}
				/>
			</Box>
		);
	};

	const renderNotesField = () => {
		return (
			<Box>
				{field.label && (
					<Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
						{field.label}
					</Typography>
				)}
				<FormField
					name={field.name || `notes-${index}`}
					render={({ input, meta }) => (
						<NotesField input={input} meta={meta} />
					)}
				/>
			</Box>
		);
	};

	const renderInput = () => {
		const type = field.type.toLowerCase();
		return (
			<Box sx={{ p: 1.5, border: '1px solid #ddd', borderRadius: 1, bgcolor: '#f9f9f9' }}>
				<Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
					{translate(type)}
				</Typography>
			</Box>
		);
	};

	// Determine which renderer to use based on field type
	if (field.type === FIELD_TITLE) {
		return (
			<Box key={`field-${index}`}>
				{renderTitle()}
			</Box>
		);
	}

	if (field.type === FIELD_DESCRIPTION) {
		return (
			<Box key={`field-${index}`}>
				{renderDescription()}
			</Box>
		);
	}

	if (field.type === FIELD_TEXT) {
		return (
			<Box key={`field-${index}`}>
				{renderText()}
			</Box>
		);
	}

	if (field.type === FIELD_TEXTAREA) {
		return (
			<Box key={`field-${index}`}>
				{renderTextarea()}
			</Box>
		);
	}

	if (field.type === FIELDS_INPUT_DECISION_RADIOGROUP) {
		return (
			<Box key={`field-${index}`}>
				{renderDecisionRadioGroup()}
			</Box>
		);
	}

	if (field.type === FIELDS_START_END_INPUTS) {
		return (
			<Box key={index}>
				{renderStartEndInputs()}
			</Box>
		);
	}

	if (field.type === FIELDS_NOTE_INPUT_FIELD) {
		return (
			<Box key={`field-${index}`}>
				{renderNotesField()}
			</Box>
		);
	}

	// Default: render generic input
	return (
		<Box key={`field-${index}`}>
			{renderInput()}
		</Box>
	);
};

export default FormFieldRenderer;
